import { useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { OriginBadge, originsOf } from "@/components/Badges";

function initForm(t) {
  const lum = t.attribuzioni.find((a) => a.tipo === "lumacofono");
  return {
    titolo: t.titolo, spiegazione: t.spiegazione, riassunto: t.riassunto || "", condizione_verifica: t.condizione_verifica,
    indizi: t.indizi.map((i) => ({ ...i, capitolo: i.capitolo ?? "" })),
    mondo: t.attribuzioni.filter((a) => a.tipo === "mondo").map((a) => ({ source_id: a.source_id })),
    lumacofono: { attiva: !!lum, nota: lum?.nota || "", links: lum?.links || [] },
  };
}

function Indizi({ f, setF, tid }) {
  const upd = (k, key, v) => setF({ ...f, indizi: f.indizi.map((i, j) => (j === k ? { ...i, [key]: v } : i)) });
  return (
    <div className="space-y-2">
      <label className="luma-label">Indizi canonici</label>
      {f.indizi.map((i, k) => (
        <div key={k} className="flex flex-wrap items-center gap-2">
          <Input value={i.testo} onChange={(e) => upd(k, "testo", e.target.value)} placeholder="Indizio" className="min-w-[200px] flex-1" data-testid={`theory-${tid}-clue-text-${k}`} />
          <Input type="number" min={1} value={i.capitolo} onChange={(e) => upd(k, "capitolo", e.target.value)} placeholder="Cap." className="w-24" data-testid={`theory-${tid}-clue-chapter-${k}`} />
          <label className="flex items-center gap-1 text-xs"><Checkbox checked={i.verificato} onCheckedChange={(v) => upd(k, "verificato", !!v)} /> verificato</label>
          <button onClick={() => setF({ ...f, indizi: f.indizi.filter((_, j) => j !== k) })}><X size={14} /></button>
        </div>
      ))}
      <Button size="sm" variant="ghost" onClick={() => setF({ ...f, indizi: [...f.indizi, { testo: "", capitolo: "", verificato: false }] })} data-testid={`theory-${tid}-add-clue`}><Plus size={14} /> Indizio</Button>
    </div>
  );
}

function Fonti({ f, setF, sources, tid }) {
  const [pick, setPick] = useState("");
  const usable = sources.filter((s) => s.stato !== "esclusa" && !f.mondo.some((m) => m.source_id === s.id));
  return (
    <div className="space-y-2">
      <label className="luma-label">Le teorie dal mondo: fonti internazionali (pubblicate solo se verificate nella scheda Fonti)</label>
      {f.mondo.map((m, k) => {
        const s = sources.find((x) => x.id === m.source_id);
        return (
          <div key={m.source_id} className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-mono">{m.source_id}</span><span className="flex-1">{s?.autore} — {s?.titolo} <i className="text-amber-300">({s?.stato})</i></span>
            <button onClick={() => setF({ ...f, mondo: f.mondo.filter((_, j) => j !== k) })}><X size={14} /></button>
          </div>
        );
      })}
      <div className="flex gap-2">
        <select className="luma-select" value={pick} onChange={(e) => setPick(e.target.value)} data-testid={`theory-${tid}-source-select`}>
          <option value="">Collega una fonte…</option>
          {usable.map((s) => <option key={s.id} value={s.id}>{s.id} · {s.autore} — {s.titolo} [{s.stato}]</option>)}
        </select>
        <Button size="sm" variant="outline" disabled={!pick} data-testid={`theory-${tid}-source-add`}
          onClick={() => { setF({ ...f, mondo: [...f.mondo, { source_id: pick }] }); setPick(""); }}>Aggiungi</Button>
      </div>
    </div>
  );
}

function Lumacofono({ f, setF, tid }) {
  const l = f.lumacofono;
  const set = (k, v) => setF({ ...f, lumacofono: { ...l, [k]: v } });
  const setLink = (k, key, v) => set("links", l.links.map((x, j) => (j === k ? { ...x, [key]: v } : x)));
  return (
    <div className="space-y-2 rounded-xl border border-violet-500/40 bg-violet-500/5 p-3">
      <label className="flex items-center gap-2 text-sm font-semibold text-violet-200"><Switch checked={l.attiva} onCheckedChange={(v) => set("attiva", v)} data-testid={`theory-${tid}-lumacofono-toggle`} /> La teoria di Lumacofono</label>
      {l.attiva && (<>
        <Textarea value={l.nota} onChange={(e) => set("nota", e.target.value)} placeholder="Commento del Fondatore" rows={2} data-testid={`theory-${tid}-lumacofono-note`} />
        {l.links.map((x, k) => (
          <div key={k} className="grid gap-2 md:grid-cols-[1fr_2fr_auto]">
            <Input value={x.titolo} onChange={(e) => setLink(k, "titolo", e.target.value)} placeholder="Titolo (YouTube, Spotify…)" data-testid={`theory-${tid}-link-title-${k}`} />
            <Input value={x.url} onChange={(e) => setLink(k, "url", e.target.value)} placeholder="https://…" data-testid={`theory-${tid}-link-url-${k}`} />
            <button onClick={() => set("links", l.links.filter((_, j) => j !== k))}><X size={14} /></button>
          </div>
        ))}
        <Button size="sm" variant="ghost" onClick={() => set("links", [...l.links, { titolo: "", url: "" }])} data-testid={`theory-${tid}-add-link`}><Plus size={14} /> Link video, puntata o fonte</Button>
      </>)}
    </div>
  );
}

export function TheoryEditor({ t, all, sources, onSaved, Problems }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(() => initForm(t));
  const [mergeTo, setMergeTo] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const run = async (fn, ok) => { try { await fn(); toast.success(ok); onSaved(); } catch (e) { toast.error(errMsg(e)); } };
  const save = () => run(() => api.put(`/admin/theories/${t.id}`, {
    ...f, lumacofono: { ...f.lumacofono, links: f.lumacofono.links.filter((x) => x.url.trim()) }, indizi: f.indizi.filter((i) => i.testo.trim()).map((i) => ({ ...i, capitolo: i.capitolo === "" ? null : parseInt(i.capitolo, 10) })) }), "Esito salvato");
  const status = (s) => run(() => api.post(`/admin/theories/${t.id}/status/${s}`), `Esito: ${s}`);
  const merge = () => run(() => api.post(`/admin/theories/${t.id}/merge`, { theory_id: mergeTo }), "Esiti uniti");
  const comm = t.attribuzioni.filter((a) => a.tipo === "community");
  return (
    <div className="luma-card space-y-3" data-testid={`theory-editor-${t.id}`}>
      <button className="flex w-full items-start justify-between gap-2 text-left" onClick={() => setOpen(!open)} data-testid={`theory-toggle-${t.id}`}>
        <span className="space-y-1">
          <span className="flex flex-wrap gap-1.5"><span className="font-mono text-xs">{t.id}</span>{originsOf(t).map((o) => <OriginBadge key={o} tipo={o} />)}
            <b className={`text-xs ${t.stato === "pubblicata" ? "text-emerald-300" : "text-amber-300"}`}>{t.stato}</b><span className="text-xs text-muted-foreground">{t.voti} voti</span></span>
          <span className="block font-semibold">{t.titolo}</span>
        </span>
        <ChevronDown size={18} className={open ? "rotate-180" : ""} />
      </button>
      {open && t.stato !== "unita" && (<>
        <Input value={f.titolo} onChange={set("titolo")} data-testid={`theory-${t.id}-title-input`} />
        <label className="luma-label">Riassunto sempre visibile (vuoto = prima frase della spiegazione)</label>
        <Textarea value={f.riassunto} onChange={set("riassunto")} rows={2} maxLength={600} data-testid={`theory-${t.id}-summary-input`} />
        <label className="luma-label">Spiegazione completa</label>
        <Textarea value={f.spiegazione} onChange={set("spiegazione")} rows={4} placeholder="Spiegazione specifica (no testo copiato)" data-testid={`theory-${t.id}-explanation-input`} />
        <Indizi f={f} setF={setF} tid={t.id} />
        <label className="luma-label">Condizione di verifica dell'esito</label>
        <Input value={f.condizione_verifica} onChange={set("condizione_verifica")} data-testid={`theory-${t.id}-condition-input`} />
        <Fonti f={f} setF={setF} sources={sources} tid={t.id} />
        <Lumacofono f={f} setF={setF} tid={t.id} />
        {comm.length > 0 && <p className="text-xs text-pink-200">Attribuzioni community (conservate): {comm.map((c) => c.autore_nome).join(", ")}</p>}
        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="rounded-full" onClick={save} data-testid={`theory-${t.id}-save`}>Salva</Button>
          {t.stato !== "pubblicata" ? <Button size="sm" variant="outline" className="rounded-full" onClick={() => status("pubblicata")} data-testid={`theory-${t.id}-publish`}>Pubblica (votabile)</Button>
            : <Button size="sm" variant="outline" className="rounded-full" onClick={() => status("bozza")} data-testid={`theory-${t.id}-unpublish`}>Ritira in bozza</Button>}
          {t.stato !== "rifiutata" && <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => status("rifiutata")} data-testid={`theory-${t.id}-reject`}>Rifiuta</Button>}
        </div>
        <div className="flex flex-wrap gap-2">
          <select className="luma-select max-w-sm" value={mergeTo} onChange={(e) => setMergeTo(e.target.value)} data-testid={`theory-${t.id}-merge-select`}>
            <option value="">Deduplica: unisci in…</option>
            {all.filter((x) => x.id !== t.id && ["bozza", "pubblicata"].includes(x.stato)).map((x) => <option key={x.id} value={x.id}>{x.titolo}</option>)}
          </select>
          <Button size="sm" variant="ghost" disabled={!mergeTo} onClick={merge} data-testid={`theory-${t.id}-merge-button`}>Unisci (voti e attribuzioni conservati)</Button>
        </div>
      </>)}
      {open && t.stato === "unita" && <p className="text-xs text-muted-foreground">Unito in {t.merged_into}.</p>}
    </div>
  );
}
